import { QuestionType } from '@features/event-registration/types';

import {
  AddToMode,
  FormFieldData,
  FormStatus,
  FormTemplate,
  TemplateId
} from '../types';

export const ALL_FORM_STATUSES: FormStatus[] = [
  'published',
  'paused',
  'closed'
];

export const ALL_ADD_TO_MODES: AddToMode[] = ['group', 'member_list'];

export const DEFAULT_LIST_FILTERS = {
  addTo: [] as AddToMode[]
};

export const FORM_STATUS_COLORS: Record<
  FormStatus,
  { bgClass: string; textClass: string }
> = {
  published: {
    bgClass: 'bg-cofeeGreen1',
    textClass: 'text-cofeeGreen8'
  },
  paused: {
    bgClass: 'bg-cofeeOrange50',
    textClass: 'text-cofeeOrange300'
  },
  closed: {
    bgClass: 'bg-cofeeRed1',
    textClass: 'text-cofeeRed7'
  }
};

export const LOCKED_BASIC_FIELDS: FormFieldData[] = [
  {
    id: 'basic-name',
    label: 'Registration name',
    type: QuestionType.TEXT,
    required: true,
    order: 1,
    isLocked: true,
    fieldCategory: 'basic',
    syncToMember: true
  },
  {
    id: 'basic-mobile',
    label: 'Mobile number',
    type: QuestionType.MOBILE_NUMBER,
    required: true,
    order: 2,
    isLocked: true,
    fieldCategory: 'basic',
    syncToMember: true
  }
];

const customField = (
  id: string,
  label: string,
  type: QuestionType,
  order: number,
  options?: Partial<FormFieldData>
): FormFieldData => ({
  id,
  label,
  type,
  required: false,
  order,
  fieldCategory: 'custom',
  ...options
});

const annualFeeCustomFields: FormFieldData[] = [
  customField('annual-grade', 'Grade / Class', QuestionType.DROP_DOWN, 3, {
    required: true,
    options: ['Grade 6', 'Grade 7', 'Grade 8', 'Grade 9', 'Grade 10']
  }),
  customField('annual-parent-email', 'Parent email', QuestionType.TEXT, 4)
];

const admissionCustomFields: FormFieldData[] = [
  customField('admission-dob', 'Date of birth', QuestionType.DATE, 3, {
    required: true
  }),
  customField('admission-gender', 'Gender', QuestionType.SINGLE_CHOICE, 4, {
    options: ['Male', 'Female', 'Prefer not to say']
  }),
  customField(
    'admission-address',
    'Residential address',
    QuestionType.TEXT_AREA,
    5
  )
];

const workshopCustomFields: FormFieldData[] = [
  customField(
    'workshop-session',
    'Preferred session',
    QuestionType.SINGLE_CHOICE,
    3,
    {
      required: true,
      options: ['Morning batch', 'Evening batch']
    }
  ),
  customField(
    'workshop-experience',
    'Prior experience level',
    QuestionType.DROP_DOWN,
    4,
    {
      options: ['Beginner', 'Intermediate', 'Advanced']
    }
  )
];

const examCustomFields: FormFieldData[] = [
  customField(
    'exam-roll',
    'Previous roll number (if any)',
    QuestionType.TEXT,
    3
  ),
  customField(
    'exam-center',
    'Preferred exam center',
    QuestionType.DROP_DOWN,
    4,
    {
      required: true,
      options: ['Center A', 'Center B', 'Center C']
    }
  )
];

export const FORM_TEMPLATES: Record<
  Exclude<TemplateId, 'blank'>,
  FormTemplate
> = {
  'annual-fee': {
    id: 'annual-fee',
    name: 'Annual Fee Collection',
    summary:
      'Collecting annual or term fees. Shared via WhatsApp broadcast, website, or institute noticeboard.',
    description: 'Collect yearly tuition or membership fees from students',
    addToMode: 'group',
    feeAmount: 12000,
    defaultFields: [...LOCKED_BASIC_FIELDS, ...annualFeeCustomFields]
  },
  'admission-registration': {
    id: 'admission-registration',
    name: 'Admission Registration Form',
    summary:
      'Institutes collecting admission registrations. Student onboarded to member list; batch/group assignment happens after registration.',
    description: 'Onboard new students with admission details and documents',
    addToMode: 'group',
    feeAmount: 2500,
    defaultFields: [...LOCKED_BASIC_FIELDS, ...admissionCustomFields]
  },
  workshop: {
    id: 'workshop',
    name: 'Workshop / Short Course',
    summary: 'One-off workshops, weekend courses, summer camps.',
    description: 'Register attendees for a short course or workshop',
    addToMode: 'member_list',
    feeAmount: 1500,
    acceptInstallments: true,
    defaultFields: [...LOCKED_BASIC_FIELDS, ...workshopCustomFields]
  },
  'exam-fee': {
    id: 'exam-fee',
    name: 'Exam / Application Fee',
    summary: 'Entrance exams, scholarship applications, registration fees.',
    description: 'Collect exam or application fees with center preferences',
    addToMode: 'member_list',
    feeAmount: 800,
    defaultFields: [...LOCKED_BASIC_FIELDS, ...examCustomFields]
  }
};

export const FORM_TEMPLATE_LIST: FormTemplate[] = [
  FORM_TEMPLATES['annual-fee'],
  FORM_TEMPLATES['admission-registration'],
  FORM_TEMPLATES.workshop,
  FORM_TEMPLATES['exam-fee']
];

export const BLANK_TEMPLATE_ID = 'blank' as const;
export type BlankTemplateId = typeof BLANK_TEMPLATE_ID;

export const DEFAULT_FORM_COVER_IMAGE_URL =
  'https://cofee-images1.s3.ap-southeast-1.amazonaws.com/default_cover_image.png';

export { MOCK_FORMS_LIST } from './mockFormsList';
